import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, formatDateShort } from '../../utils/dateUtils';
import './CalendarDatePicker.css';

export interface CalendarDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  id?: string;
  label?: string;
  className?: string;
  dateDisplayFormat?: 'default' | 'short';
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_HEADER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function toDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getCalendarGrid(year: number, month: number): { date: Date; isCurrentMonth: boolean }[] {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  const grid: { date: Date; isCurrentMonth: boolean }[] = [];
  for (let i = 0; i < startDow; i++) {
    const d = daysInPrevMonth - startDow + 1 + i;
    grid.push({ date: new Date(prevYear, prevMonth, d), isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }
  const remaining = 42 - grid.length;
  for (let d = 1; d <= remaining; d++) {
    grid.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
  }
  return grid;
}

export const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  id,
  label,
  className = '',
  dateDisplayFormat = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const d = value ? toDate(value) : new Date();
    return (d || new Date()).getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? toDate(value) : new Date();
    return (d || new Date()).getMonth();
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');

  const valueDate = toDate(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (open && value) {
      const d = toDate(value);
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const displayText = value && valueDate
    ? (dateDisplayFormat === 'short' ? formatDateShort(value) : formatDate(value))
    : placeholder;
  const grid = getCalendarGrid(viewYear, viewMonth);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDate = (d: Date) => {
    onChange(toISO(d));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const handleToday = () => {
    onChange(toISO(today));
    setOpen(false);
  };

  const isDisabled = (d: Date): boolean => {
    const iso = toISO(d);
    if (minDate && iso < minDate) return true;
    if (maxDate && iso > maxDate) return true;
    return false;
  };

  return (
    <div className={`calendar-date-picker ${className}`.trim()} ref={containerRef}>
      {label && <label className="calendar-date-picker-label" htmlFor={id}>{label}</label>}
      <button
        type="button"
        id={id}
        className="calendar-date-picker-trigger"
        data-format={dateDisplayFormat}
        data-value={value || undefined}
        onClick={() => {
          if (!open) {
            const rect = containerRef.current?.getBoundingClientRect();
            const popoverWidth = 280;
            if (rect && window.innerWidth - rect.right < popoverWidth) {
              setPopoverAlign('right');
            } else {
              setPopoverAlign('left');
            }
          }
          setOpen((o) => !o);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label ? `${label}: ${displayText}` : displayText}
      >
        <CalendarIcon size={18} strokeWidth={2} className="calendar-date-picker-trigger-icon" aria-hidden />
        <span className="calendar-date-picker-trigger-text">{displayText}</span>
      </button>

      {open && (
        <div
          className={`calendar-date-picker-popover ${popoverAlign === 'right' ? 'calendar-date-picker-popover-right' : ''}`}
          role="dialog"
          aria-label="Choose date"
        >
          <div className="calendar-date-picker-header">
            <div className="calendar-date-picker-month-year">
              <span>{MONTHS[viewMonth]} {viewYear}</span>
              <ChevronDown size={16} strokeWidth={2} aria-hidden />
            </div>
            <div className="calendar-date-picker-nav">
              <button type="button" className="calendar-date-picker-nav-btn" onClick={goPrevMonth} aria-label="Previous month">
                <ChevronLeft size={18} strokeWidth={2} />
              </button>
              <button type="button" className="calendar-date-picker-nav-btn" onClick={goNextMonth} aria-label="Next month">
                <ChevronRight size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="calendar-date-picker-days-header">
            {DAYS_HEADER.map((day) => (
              <span key={day} className="calendar-date-picker-day-name">{day}</span>
            ))}
          </div>

          <div className="calendar-date-picker-grid">
            {grid.map(({ date, isCurrentMonth }, i) => {
              const selected = isSameDay(date, valueDate);
              const isTodayCell = isSameDay(date, today);
              const disabled = isDisabled(date);
              return (
                <button
                  key={i}
                  type="button"
                  className={`calendar-date-picker-cell ${!isCurrentMonth ? 'other-month' : ''} ${selected ? 'selected' : ''} ${isTodayCell ? 'today' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => !disabled && handleSelectDate(date)}
                  disabled={disabled}
                  aria-label={date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="calendar-date-picker-footer">
            <button type="button" className="calendar-date-picker-footer-clear" onClick={handleClear}>
              CLEAR
            </button>
            <button type="button" className="calendar-date-picker-footer-today" onClick={handleToday}>
              TODAY
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
